import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows outside the main buy buttons on mobile, and after them on desktop.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.addEventListener('change', this.#handleStickyVariantSelectChange, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#syncPageStickyState(false);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Scope the observer to the page footer. PDP protocol modals also render
    // semantic <footer> elements, but those are hidden and must not dismiss
    // the sticky purchase bar as soon as it opens.
    const footer =
      document.querySelector('#footer-group footer') ??
      document.querySelector('.shopify-section-group-footer-group footer') ??
      document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    const mobileViewport = window.matchMedia('(max-width: 749px)');
    let footerVisible = false;
    const updateVisibility = () => {
      const rect = buyButtonsBlock.getBoundingClientRect();
      const aboveViewport = rect.bottom <= 0;
      const belowViewport = rect.top >= window.innerHeight;
      const shouldShow = !footerVisible && (aboveViewport || (mobileViewport.matches && belowViewport));

      if (shouldShow === this.#isStuck) return;
      if (shouldShow) this.#showStickyBar();
      else this.#hideStickyBar();
    };

    // Mobile starts with a purchase action, hides it beside the main form,
    // and restores it below that form. Desktop keeps its existing behavior.
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(updateVisibility);
    mobileViewport.addEventListener('change', updateVisibility, { signal: this.#abortController.signal });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        footerVisible = entry.isIntersecting;
        updateVisibility();
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Opens or focuses the main variant selector from the sticky selector surface.
   */
  handleVariantTriggerClick = () => {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });

    window.setTimeout(() => {
      const select = variantPicker.querySelector('select:not([disabled])');
      if (select instanceof HTMLSelectElement) {
        select.focus({ preventScroll: true });
        if (typeof select.showPicker === 'function') {
          try {
            select.showPicker();
          } catch (_error) {
            // Browser can block showPicker outside the original user gesture.
          }
        }
        return;
      }

      const checkedInput = variantPicker.querySelector('input:checked');
      const focusTarget = checkedInput?.closest('label') || variantPicker.querySelector('label, button, input');
      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({ preventScroll: true });
      }
    }, 260);
  };

  /**
   * Keyboard activation for the sticky selector surface.
   * @param {KeyboardEvent} event
   */
  handleVariantTriggerKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.handleVariantTriggerClick();
  };

  /**
   * Syncs the sticky native variant select with the main variant picker.
   * @param {Event} event
   */
  handleVariantSelectChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const selectedOption = event.target.selectedOptions[0];
    if (!selectedOption) return;

    this.dataset.currentVariantId = selectedOption.value;
    this.#syncMainVariantPicker(selectedOption);
  };

  /**
   * Handles native sticky select changes without relying on declarative event hydration.
   * @param {Event} event
   */
  #handleStickyVariantSelectChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.classList.contains('sticky-add-to-cart__variant-select')) return;

    this.handleVariantSelectChange(event);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncPageStickyState(true);
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#syncPageStickyState(false);
  }

  /**
   * Exposes the sticky state so third-party floating launchers can clear the
   * two-row mobile purchase bar without coupling this component to an app.
   * @param {boolean} active
   */
  #syncPageStickyState(active) {
    if (!document.querySelector('.crown-pdp-layout')) return;
    document.documentElement.classList.toggle('crown-pdp-sticky-active', active);
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the product's variant picker element.
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} variant-picker[data-product-id="${productId}"], #shopify-section-${sectionId} variant-picker`
    );
  }

  /**
   * Applies a sticky select option to the main variant picker and lets the
   * native variant-picker component fetch/morph price, media and form state.
   * @param {HTMLOptionElement} stickyOption
   */
  #syncMainVariantPicker(stickyOption) {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const optionValues = [stickyOption.dataset.option1, stickyOption.dataset.option2, stickyOption.dataset.option3].filter(
      (value) => value !== undefined && value !== ''
    );
    if (!optionValues.length) return;

    const optionControls = Array.from(
      variantPicker.querySelectorAll('fieldset[data-fieldset-index], .variant-option--dropdowns select')
    );
    /** @type {HTMLInputElement | HTMLSelectElement | null} */
    let triggerControl = null;

    optionValues.forEach((value, index) => {
      const optionControl = optionControls[index];

      if (optionControl instanceof HTMLFieldSetElement) {
        const input = Array.from(optionControl.querySelectorAll('input')).find((candidate) => candidate.value === value);
        if (input instanceof HTMLInputElement) {
          input.checked = true;
          triggerControl = input;
        }
        return;
      }

      if (optionControl instanceof HTMLSelectElement) {
        const matchingOption = Array.from(optionControl.options).find((option) => option.value === value);
        if (matchingOption) {
          optionControl.value = value;
          Array.from(optionControl.options).forEach((option) => option.removeAttribute('selected'));
          matchingOption.setAttribute('selected', 'selected');
          triggerControl = optionControl;
        }
      }
    });

    if (!triggerControl) return;

    if (triggerControl instanceof HTMLInputElement) {
      triggerControl.dataset.variantId = stickyOption.value;
    } else {
      const selectedMainOption = triggerControl.options[triggerControl.selectedIndex];
      if (selectedMainOption) {
        selectedMainOption.dataset.variantId = stickyOption.value;
      }
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', stickyOption.value);
    history.replaceState({}, '', url.toString());

    triggerControl.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
